import { skipToken } from '@reduxjs/toolkit/query';
import Loader from 'components/Loader';
import { useIsNewDesign } from 'components/tables/list-table/filter-bar/useIsNewDesign';
import { useMemo } from 'react';
import { Navigate, useParams } from 'react-router';
import { useGetPracticeAreaByIdQuery } from 'store/api/practice-areas';
import { PracticeAreaForm } from 'types/practice-areas';
import { useClassifiers } from 'utils/useClassifiers';
import { PRACTICE_AREA_FORM_CLASSIFIERS } from './form-sections/usePracticeAreaForm';
import { practiceAreaDtoToForm } from './practiceAreaFormMapping';
import PracticeAreaPageForm from './PracticeAreaPageForm';

const ClassicPracticeAreaPageEdit = () => {
    const { id: practiceAreaId } = useParams();

    const { classifiers, isLoading: isLoadingClassifiers } = useClassifiers({
        codes: PRACTICE_AREA_FORM_CLASSIFIERS,
    });

    const {
        data: practiceArea,
        isLoading,
        isError,
        refetch,
    } = useGetPracticeAreaByIdQuery(practiceAreaId ?? skipToken);

    // Derive initial form data from fetched practice area + classifiers (during render).
    const initialData = useMemo<PracticeAreaForm | undefined>(() => {
        if (!classifiers) return undefined;
        return practiceAreaDtoToForm(practiceArea);
    }, [classifiers, practiceArea]);

    if (isLoading || isLoadingClassifiers || (initialData === undefined && !isError)) {
        return <Loader />;
    }

    return (
        <PracticeAreaPageForm
            type="edit"
            initialData={initialData}
            classifiers={classifiers}
            refetch={() => {
                void refetch();
            }}
            entityCustomFields={practiceArea?.customFields}
        />
    );
};

/** In the new design a practice area is edited card by card on its page, so its edit URL opens the first card there. */
const PracticeAreaPageEdit = () => {
    const isNew = useIsNewDesign();
    const { id: practiceAreaId } = useParams();
    return isNew ? (
        <Navigate to={`/practice-area/${practiceAreaId}?edit=practice-area`} replace />
    ) : (
        <ClassicPracticeAreaPageEdit />
    );
};

export default PracticeAreaPageEdit;
